import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { SEO } from "../components/SEO";
import { ChevronRight, MapPin, Loader2 } from "lucide-react";
import CarCard from "../components/CarCard";
import { useLanguage } from "../contexts/LanguageContext";
import odooApi from "../services/odooApi";

export const CityPage = () => {
  const { slug } = useParams();
  const { t, tf } = useLanguage();
  const [city, setCity] = useState(null);
  const [cars, setCars] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [cityData, cityCars] = await Promise.all([
          odooApi.getCity(slug),
          odooApi.getCityCars(slug),
        ]);
        setCity(cityData);
        setCars(cityCars);
      } catch (err) {
        console.error("Failed to load city:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white dark:bg-gray-950">
        <Loader2 className="w-10 h-10 animate-spin text-[#D4AF37]" />
      </div>
    );
  }

  if (!city) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white dark:bg-gray-950">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">{t("cityPage.notFound", "City Not Found")}</h2>
          <Link to="/cars" className="text-[#D4AF37] hover:underline">{t("carDetail.browseAll", "Browse all cars")}</Link>
        </div>
      </div>
    );
  }

  return (
    <>
      <SEO
        title={`Car Rental in ${city.name} | Luxury & Economy Cars`}
        description={`Rent luxury and economy cars in ${city.name}, UAE. ${city.carCount}+ vehicles available from top suppliers. Best prices and instant booking with Velaris Royal.`}
        keywords={`car rental ${city.name.toLowerCase()}, rent car ${city.name.toLowerCase()}, luxury car hire ${city.name.toLowerCase()}, ${city.name.toLowerCase()} car rental company`}
        image={city.image}
        breadcrumbs={[
          { name: t("nav.home", "Home"), url: "/" },
          { name: t("nav.cities", "Cities"), url: "/cars" },
          { name: city.name },
        ]}
      />

      <div className="min-h-screen bg-white dark:bg-gray-950" data-testid="city-page">
        <div className="relative h-[40vh] min-h-[300px] bg-black">
          <img src={city.image} alt={city.name} className="absolute inset-0 w-full h-full object-cover opacity-60" />
          <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent" />
          <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-full flex flex-col justify-end pb-12">
            <nav className="flex items-center gap-2 text-sm mb-4">
              <Link to="/" className="text-white/60 hover:text-[#D4AF37]">{t("nav.home", "Home")}</Link>
              <ChevronRight className="w-4 h-4 text-white/40" />
              <Link to="/cars" className="text-white/60 hover:text-[#D4AF37]">{t("nav.cities", "Cities")}</Link>
              <ChevronRight className="w-4 h-4 text-white/40" />
              <span className="text-white">{city.name}</span>
            </nav>
            <div className="flex items-center gap-3 mb-4">
              <MapPin className="w-6 h-6 text-[#D4AF37]" />
              <h1 className="font-[var(--font-display)] text-3xl sm:text-4xl lg:text-5xl font-bold text-white">
                {tf("cityPage.rentalIn", { city: city.name }, `Car Rental in ${city.name}`)}
              </h1>
            </div>
            <p className="text-white/70 max-w-2xl">{city.description}</p>
          </div>
        </div>

        <div className="bg-black border-t border-[#D4AF37]/30">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
            <div className="flex items-center justify-center gap-8">
              <div className="text-center">
                <p className="text-2xl font-bold text-gold-gradient">{city.carCount || cars.length}</p>
                <p className="text-white/60 text-sm">{t("common.carsAvailable", "Cars Available")}</p>
              </div>
              <div className="w-px h-12 bg-[#D4AF37]/30" />
              <div className="text-center">
                <p className="text-2xl font-bold text-gold-gradient">24/7</p>
                <p className="text-white/60 text-sm">{t("common.support", "Support")}</p>
              </div>
              <div className="w-px h-12 bg-[#D4AF37]/30" />
              <div className="text-center">
                <p className="text-2xl font-bold text-gold-gradient">{city.supplierCount || '50+' }</p>
                <p className="text-white/60 text-sm">{t("common.suppliers", "Suppliers")}</p>
              </div>
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="flex items-center justify-between mb-8">
            <h2 className="font-[var(--font-display)] text-2xl font-bold text-gray-900 dark:text-white">
              {tf("cityPage.availableCarsIn", { city: city.name }, `Available Cars in ${city.name}`)}
            </h2>
            <Link to={`/cars?city=${encodeURIComponent(city.slug || slug)}`} className="text-[#D4AF37] hover:text-[#C5A028] font-medium flex items-center gap-1">
              {t("btn.viewAll", "View All")}<ChevronRight className="w-4 h-4" />
            </Link>
          </div>

          {cars.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6" data-testid="city-cars-grid">
              {cars.map((car) => (<CarCard key={car.id} car={car} />))}
            </div>
          ) : (
            <div className="text-center py-16 text-gray-500 dark:text-gray-400">
              <p className="text-lg">{tf("cityPage.empty", { city: city.name }, `No cars available in ${city.name} at the moment. Check back soon!`)}</p>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default CityPage;

